import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import omit from 'lodash/omit';

import { jsonParse } from '@lyearn/core/utils/json';
import { TFunction } from '@lyearn/i18n';
import { DEFAULT_LOCATION_PROPS } from '@/components/Form/components/AddLocation/consts';
import { AppConfig } from '@/hooks/useAppConfig';
import { getCurrentDateRoundToHour } from '@/modules/classrooms/modules/events/utils/eventTime';
import { getDefaultCertificateSettings } from '@/modules/corporate/modules/certificates/consts';
import { adaptTagsOnEdit, DEFAULT_TAG_VALUE } from '@/modules/corporate/modules/tags/utils';
import { DEFAULT_WORKSPACE_IDS } from '@/modules/corporate/modules/workspaceSettings/const';
import {
  CompletionSettingsInput,
  CreateEventInput,
  EventSubTypeEnum,
  EventTypeEnum,
  EventUnion,
  EventWorkContentInput,
  ExternalEventCreationStrategyEnum,
  ExternalEventProps,
  ExternalEventProviderInfoInput,
  ExternalEventType,
  LiveEventType,
  Maybe,
  OfflineEventType,
  RecurringTypeEnum,
  ScheduleInfoInput,
} from '@/types/schema';
import { getRandomCardImage } from '@/utils/cardImage';
import { getCurrentTimeZoneValue, getUtcToZonedTime } from '@/utils/timezone';

import {
  ABSENT_THRESHOLD_DEFAULT_VALUE,
  COMPLETION_THRESHOLD_DEFAULT_VALUE,
} from '../components/Editor/components/EventAttendanceSettings/consts';
import { EventFormInput } from '../types';

const getDefaultStartTime = () => {
  return formatISO(addHours(getCurrentDateRoundToHour(), 1));
};

const getDefaultEndTime = () => {
  return formatISO(addHours(parseISO(getDefaultStartTime()), 1));
};

export const getEventCompletionSettingsDefaultValue = (
  settings?: Maybe<CompletionSettingsInput>,
) => ({
  considerRecordingProgress: settings?.considerRecordingProgress ?? true,
  requirePreWorkPostWork: settings?.requirePreWorkPostWork ?? false,
  recurringSessionCompletionRollupType: settings?.recurringSessionCompletionRollupType ?? undefined,
  attendanceRecordingCompletionThreshold:
    settings?.attendanceRecordingCompletionThreshold ?? COMPLETION_THRESHOLD_DEFAULT_VALUE,
  attendanceRecordingAbsentThreshold:
    settings?.attendanceRecordingAbsentThreshold ?? ABSENT_THRESHOLD_DEFAULT_VALUE,
});

const EXTERNAL_EVENT_DEFAULT_PROPS: ExternalEventProps = {
  externalEventProviderInfo: {
    providerId: '',
    meta: '{}',
    creationStrategy: ExternalEventCreationStrategyEnum.Form,
  },
  recordingSettings: {
    automaticRecording: false,
    shareRecording: false,
  },
  enableWaitingRoom: true,
};

const DEFAULT_PRE_POST_IDS_CONTENT: EventWorkContentInput[] = [];

const getInitialValues = (
  site: AppConfig['site'],
  eventType = EventTypeEnum.Live,
): EventFormInput => {
  return {
    type: eventType,
    name: '',
    objective: '',
    description: '',
    startTime: getDefaultStartTime(),
    endTime: getDefaultEndTime(),
    defaultCover: getRandomCardImage('EVENT'),
    instructorIds: [],
    skillIds: [],
    certificateSettings: getDefaultCertificateSettings({
      disableLinkedInSharing: site.certificateSharingSettings.disableLinkedInSharing,
      disableSocialSharing: site.certificateSharingSettings.disableSocialSharing,
    }),
    registrationSettings: {},
    liveEventProps:
      eventType === EventTypeEnum.Live
        ? {
            recordingSettings: {
              automaticRecording: false,
              shareRecording: getEventCompletionSettingsDefaultValue().considerRecordingProgress,
            },
            subType: EventSubTypeEnum.GroupSession,
          }
        : undefined,
    offlineEventProps: eventType === EventTypeEnum.Offline ? DEFAULT_LOCATION_PROPS : undefined,
    visibilitySettings: site.defaultContentVisibilitySettings,
    externalEventProps:
      eventType === EventTypeEnum.External ? EXTERNAL_EVENT_DEFAULT_PROPS : undefined,
    tags: DEFAULT_TAG_VALUE,
    prework: {
      set: { contents: DEFAULT_PRE_POST_IDS_CONTENT },
    },
    postwork: {
      set: { contents: DEFAULT_PRE_POST_IDS_CONTENT },
    },
    subWorkspaceIds: DEFAULT_WORKSPACE_IDS,
    timezone: getCurrentTimeZoneValue(),
    completionSettings: getEventCompletionSettingsDefaultValue(),
  };
};

export const getResolvedType = (type: EventTypeEnum) => {
  switch (type) {
    case EventTypeEnum.RecurringLive:
      return EventTypeEnum.Live;
    case EventTypeEnum.RecurringExternal:
      return EventTypeEnum.External;
    case EventTypeEnum.RecurringOffline:
      return EventTypeEnum.Offline;
    default:
      return type;
  }
};

export const getInitialFormValues = (
  site: AppConfig['site'],
  event?: EventUnion,
  eventType?: EventTypeEnum,
): EventFormInput => {
  if (!event) {
    return getInitialValues(site, eventType);
  }

  const {
    name,
    objective,
    description,
    startTime,
    endTime,
    cover,
    instructorIds,
    skillIds,
    completionSettings,
    certificateSettings,
    registrationSettings,
    visibilitySettings,
    type: typeProp,
    tags,
    prework,
    postwork,
    subWorkspaces,
    timezone,
    recurringCategory,
    recurrenceSchedule,
  } = event;

  const type = getResolvedType(typeProp);

  const liveEventProps =
    type === EventTypeEnum.Live
      ? {
          recordingSettings: (event as LiveEventType).recordingSettings,
          subType: (event as LiveEventType).subType,
        }
      : undefined;

  const offlineEventProps =
    type === EventTypeEnum.Offline ? { location: (event as OfflineEventType).location } : undefined;

  const externalEventProps =
    type === EventTypeEnum.External
      ? {
          externalEventProviderInfo: {
            ...(event as ExternalEventType).externalEventProviderInfo,
          },
          recordingSettings: (event as ExternalEventType).recordingSettings,
          enableWaitingRoom: (event as ExternalEventType).enableWaitingRoom,
        }
      : undefined;

  const resolvedTimezone = getResolvedTimezone(timezone);

  return {
    type,
    name,
    objective,
    description: description ?? '',
    startTime: getUtcToZonedTime(resolvedTimezone, startTime),
    endTime: getUtcToZonedTime(resolvedTimezone, endTime),
    cover,
    instructorIds: instructorIds ?? [],
    skillIds: skillIds ?? [],
    certificateSettings:
      certificateSettings ??
      getDefaultCertificateSettings({
        disableLinkedInSharing: site.certificateSharingSettings.disableLinkedInSharing,
        disableSocialSharing: site.certificateSharingSettings.disableSocialSharing,
      }),
    registrationSettings,
    visibilitySettings: visibilitySettings ?? site.defaultContentVisibilitySettings,
    completionSettings: getEventCompletionSettingsDefaultValue(completionSettings),
    liveEventProps,
    offlineEventProps,
    tags: adaptTagsOnEdit(tags),
    externalEventProps,
    defaultCover: getRandomCardImage('EVENT'),
    prework: {
      set: { contents: prework?.contents || DEFAULT_PRE_POST_IDS_CONTENT },
    },
    postwork: {
      set: { contents: postwork?.contents || DEFAULT_PRE_POST_IDS_CONTENT },
    },
    subWorkspaceIds: subWorkspaces,
    timezone: resolvedTimezone,
    recurringCategory,
    recurrenceSchedule,
  };
};

const PAGE_TITLE_MAP = {
  create: {
    [EventTypeEnum.Live]: 'Events:Editor.NewClass',
    [EventTypeEnum.Offline]: 'Events:Editor.NewOfflineSession',
    [EventTypeEnum.External]: 'Events:Editor.NewExternalSession',
    [EventTypeEnum.RecurringLive]: 'Events:Editor.NewClass',
    [EventTypeEnum.RecurringOffline]: 'Events:Editor.NewOfflineSession',
    [EventTypeEnum.RecurringExternal]: 'Events:Editor.NewExternalSession',
  },
  edit: {
    [EventTypeEnum.Live]: 'Events:Editor.EditClass',
    [EventTypeEnum.Offline]: 'Events:Editor.EditOfflineSession',
    [EventTypeEnum.External]: 'Events:Editor.EditExternalSession',
    [EventTypeEnum.RecurringLive]: 'Events:Editor.EditClass',
    [EventTypeEnum.RecurringOffline]: 'Events:Editor.EditOfflineSession',
    [EventTypeEnum.RecurringExternal]: 'Events:Editor.EditExternalSession',
  },
};

export const getPageTitle = (t: TFunction, type: EventTypeEnum, isEditMode: boolean) => {
  return isEditMode ? t(PAGE_TITLE_MAP.edit[type]) : t(PAGE_TITLE_MAP.create[type]);
};

interface Params {
  t: TFunction;
  type: EventTypeEnum;
  formValues: EventFormInput;
}

export const getEventDuplicateFormValues = (params: Params): EventFormInput => {
  const { formValues, type, t } = params;

  let { name, timezone, cover, recurringCategory } = formValues;
  name = t('Events:CopyOf') + name;

  const resolvedTimezone = !timezone || timezone === 'UTC' ? getCurrentTimeZoneValue() : timezone;

  const currentTime = getCurrentDateRoundToHour();
  const startDay = addDays(currentTime, 2);
  const endTime = getUtcToZonedTime(resolvedTimezone, addHours(startDay, 2).toISOString());
  const startTime = getUtcToZonedTime(resolvedTimezone, startDay.toISOString());

  return {
    ...formValues,
    cover: omit(cover, 'mediaId'),
    name,
    startTime,
    endTime,
    type,
    timezone: resolvedTimezone,
    recurringCategory:
      recurringCategory === RecurringTypeEnum.RecurringInstance
        ? RecurringTypeEnum.NonRecurring
        : recurringCategory,
  };
};

export const getProviderMetaObj = (value?: Pick<ExternalEventProviderInfoInput, 'meta'> | null) => {
  if (!value) {
    return {};
  }

  if (value.meta) {
    const { data, error } = jsonParse<any>(value.meta);
    if (error) {
      return {};
    }

    return data;
  }

  return {};
};

export const adaptRecurrenceScheduleInput = (
  values: CreateEventInput,
): ScheduleInfoInput | undefined => {
  if (!values.recurrenceSchedule || values.recurringCategory !== RecurringTypeEnum.Recurring) {
    return undefined;
  }

  return {
    ...values.recurrenceSchedule,
    startDate: values.startTime,
    time: format(parseISO(values.startTime), 'HH:mm'),
    timezone: values.timezone || getCurrentTimeZoneValue(),
  };
};

export const adaptEventType = (
  type?: EventTypeEnum,
  recurringCategory?: RecurringTypeEnum | null,
) => {
  if (recurringCategory !== RecurringTypeEnum.Recurring) {
    return type;
  }

  switch (type) {
    case EventTypeEnum.Live:
      return EventTypeEnum.RecurringLive;
    case EventTypeEnum.External:
      return EventTypeEnum.RecurringExternal;
    case EventTypeEnum.Offline:
      return EventTypeEnum.RecurringOffline;
    default:
      return type;
  }
};

export const getResolvedTimezone = (timezone?: string) =>
  !timezone || timezone === 'UTC' ? getCurrentTimeZoneValue() : timezone;
